// React
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
// Design System (@lux)
import { DarkModeProvider } from '@lux/core';
import { PlusTierPill, Size } from '@lux/plus-badge';
import { ButtonColor, ButtonType } from '@lux/buttons';
import { Text, Preset, FunctionColor } from '@lux/text';
import { CrossStrokeXS } from '@lux/zds-icons';
// Pegasus
import { Translate, TranslateScope, useTranslate } from '@pegasus/translation';
import { setCookie } from '@pegasus/utils';
// Browsing
import { trackSelectContent, trackViewContent } from '@browsing/tracking';
// Local
import { BannerWrapper, BannerContent, CloseButtonRow, PlusBadgeSection, CloseButton, TermsLink, DescriptionSection, ContentWrapper, FooterColumn, ClaimActionButton, } from './birthday-voucher-banner.styles';
import { BirthdayVoucherTermsAndConditions } from '../modal/birthday-voucher-terms-and-conditions';
import { BIRTHDAY_VOUCHER_BANNER_COOKIE, formatISODateToMMDDYYYY } from '../../utils';
import { ONE_DAY } from '../../constants';
var trackEvent = function (contentId) {
    trackSelectContent('birthday_voucher_banner', 'click', { contentId: contentId });
};
export var BirthdayVoucherBanner = function (_a) {
    var onVisibilityChange = _a.onVisibilityChange, customerIncentive = _a.customerIncentive, tier = _a.tier, displayName = _a.displayName, _b = _a.isMobileScreen, isMobileScreen = _b === void 0 ? false : _b, onShowFlashMessage = _a.onShowFlashMessage;
    var _c = useState(false), showTermsModal = _c[0], setShowTermsModal = _c[1];
    var _d = useInView({ threshold: 0.1, triggerOnce: true }), intersectionRef = _d.ref, inView = _d.inView;
    var translate = useTranslate('mylounge', { root: true });
    var formattedEndDate = useMemo(function () { return (customerIncentive.end_at ? formatISODateToMMDDYYYY(customerIncentive.end_at) : ''); }, [customerIncentive.end_at]);
    var handleShowTerms = useCallback(function () {
        setShowTermsModal(true);
        trackEvent('terms_and_conditions');
    }, []);
    var handleCloseTerms = useCallback(function () {
        setShowTermsModal(false);
    }, []);
    var handleCloseBanner = useCallback(function () {
        setCookie(BIRTHDAY_VOUCHER_BANNER_COOKIE, 'dismissed', Date.now() + ONE_DAY * 3, '/');
        onVisibilityChange === null || onVisibilityChange === void 0 ? void 0 : onVisibilityChange(false);
    }, [onVisibilityChange]);
    var handleClaimClick = useCallback(function () {
        var couponCode = customerIncentive.coupon.code;
        // Check if coupon incentive is available
        if (!customerIncentive.coupon.code) {
            return;
        }
        // Always set banner cookie to track that user interacted with banner
        setCookie(BIRTHDAY_VOUCHER_BANNER_COOKIE, 'claimed', Date.now() + ONE_DAY * 3, '/');
        // Check if coupon is already applied
        if (customerIncentive.coupon.is_applied) {
            var successMessage_1 = translate('birthdayVoucher.banner.successMessage');
            onShowFlashMessage === null || onShowFlashMessage === void 0 ? void 0 : onShowFlashMessage(successMessage_1);
            onVisibilityChange === null || onVisibilityChange === void 0 ? void 0 : onVisibilityChange(false);
            return;
        }
        // Set the coupon cookie that checkout middleware will detect (3 days expiry)
        setCookie('coupon', couponCode, Date.now() + ONE_DAY * 3, '/');
        trackEvent('claim_voucher');
        // Show success message
        var successMessage = translate('birthdayVoucher.banner.successMessage');
        onShowFlashMessage === null || onShowFlashMessage === void 0 ? void 0 : onShowFlashMessage(successMessage);
        onVisibilityChange === null || onVisibilityChange === void 0 ? void 0 : onVisibilityChange(false);
    }, [customerIncentive, translate, onVisibilityChange, onShowFlashMessage]);
    useEffect(function () {
        if (inView) {
            trackViewContent('birthday_voucher_banner');
        }
    }, [inView]);
    return (React.createElement(TranslateScope, { path: "birthdayVoucher" },
        React.createElement(DarkModeProvider, null,
            React.createElement(BannerWrapper, { ref: intersectionRef },
                React.createElement(BannerContent, null,
                    React.createElement("div", null,
                        React.createElement(CloseButtonRow, null,
                            React.createElement(CloseButton, { onClick: function () {
                                    trackEvent('close');
                                    handleCloseBanner();
                                } },
                                React.createElement(CrossStrokeXS, { color: FunctionColor.function100 }))),
                        React.createElement(PlusBadgeSection, null,
                            React.createElement(TranslateScope, { path: "plus.loyalty" },
                                React.createElement(PlusTierPill, { tier: tier, size: Size.S },
                                    React.createElement(Translate, { message: "tier.".concat(tier) }))),
                            !isMobileScreen && (React.createElement(Text, { preset: Preset.L, color: FunctionColor.function100, bold: true },
                                React.createElement(Translate, { message: "banner.title", params: { customerName: displayName } }))))),
                    React.createElement(ContentWrapper, null,
                        React.createElement(DescriptionSection, null,
                            isMobileScreen && (React.createElement(Text, { preset: Preset.L, color: FunctionColor.function100, bold: true },
                                React.createElement(Translate, { message: "banner.title", params: { customerName: displayName } }))),
                            React.createElement(Text, { preset: Preset.M, color: FunctionColor.function100 },
                                React.createElement(Translate, { message: "banner.celebrationMessage", params: {
                                        discountPercent: customerIncentive.content.discount_label,
                                    } }))),
                        React.createElement(FooterColumn, null,
                            React.createElement(Text, { preset: Preset.XS, color: FunctionColor.function80 },
                                React.createElement(Translate, { message: "banner.validUntil" }),
                                " ",
                                formattedEndDate),
                            React.createElement(TermsLink, { preset: Preset.XS, color: FunctionColor.function80, onClick: handleShowTerms },
                                React.createElement(Translate, { message: "banner.termsAndConditions" })))),
                    React.createElement(ClaimActionButton, { buttonType: ButtonType.contextual, color: ButtonColor.dark, "$isMobile": isMobileScreen, onClick: handleClaimClick },
                        React.createElement(Translate, { message: "claimForm.claiming" }))))),
        React.createElement(BirthdayVoucherTermsAndConditions, { open: showTermsModal, onClose: handleCloseTerms, customerIncentive: customerIncentive })));
};
export default BirthdayVoucherBanner;
